/* eslint-disable linebreak-style */
import {Box, Divider, FormControl, Grid, InputLabel, MenuItem, Select, useMediaQuery} from '@mui/material'
import { LoopOutlined as LoopOutlinedIcon } from '@mui/icons-material'
import { addDays, format } from 'date-fns'
import {useDispatch, useSelector, batch} from 'react-redux'
import {useEffect, useState} from 'react'
import Tab, { TabDetails } from '../../../shared/UI/Tab'
import FindPerson from './FindPerson'
import { isMobileDown } from '../../../theme/deviceChecks'
import { RootStore } from '../../../redux/store'
import ButtonSmall from '../../../shared/UI/ButtonSmall'
import DatePicker from '../../../shared/UI/DatePicker'
import {
  BookingFloorPlan,
  BookingLocation,
  BookingZone,
} from '../../../services/booking/types'
import DropdownMenu from '../../../shared/UI/DropdownMenu'
import Button from '../../../shared/UI/Button'
import {
  setBookingFloorplanViewingDate,
  setBookingGridViewingDate,
  setBookingSliderPosition,
  setDeskBookingFloorplanManuallySelected,
  setDeskBookingFocussedZoneID,
  setDeskBookingLoading,
  setDeskBookingDashboardResults,
  setDailyBookings,
  setDailyBookingsLoading,
  setDeskBookingShowGridView,
  setDeskBookingShowFloorplan,
} from '../../../redux/reducers/deskBookingReducer'
import { showErrorMessage } from '../../../redux/reducers/snackbarReducer'
import BookingErrorMessage from '../utils/BookingErrorMessage'
import { SelectOption } from '../../../services/dashboardService'
import { bookingService } from '../../../services/booking/bookingService'
import { combineDateAndTime, getJson, removeOneSecond } from '../utils/utils'
import { BookingForControls } from './BookingForControls'
import { useBookingFor } from './useBookingFor'
import Paragraph from '../../../shared/UI/Paragraph'
import LoadingIndicator from '../../../shared/UI/LoadingIndicator'
import DateRangePicker from '../../../shared/UI/DateRangePicker'
import BookingWeekdayPicker from './BookingWeekdayPicker'
import { BookingSearchType, Time, BookingSliderIndex } from './enums'
import {
  setBookingWizardBookingRangeState,
  setBookingWizardBookingsState,
  setBookingWizardFinalSelectionState,
  setBookingWizardSelectedDatesState,
  setBookingWizardSelectedFeatureState,
  setBookingWizardStepState,
  setBookingWizardWeekdaysSelectedState,
} from '../../../redux/reducers/deskBookingWizardReducer'
import { BookingDateRange, FULL_DAY_RANGE, ManagerBookingOptions, BOOKING_FOR_OPTIONS } from '../consts'
import { BookingWizardSteps } from '../BlockBooking/enums'
import { checkIsOpenFloorplanArea, getDatesFromRange } from './bookingFloorplanLogic'
import { showComponents } from '../utils'
import { BookinResetSearch } from './components'
import { BookingPeriods } from '../Popover/enums'
import { BookingSliderPosition } from '../../../utils/constants'
import {
  setDeskBookingSearchParams,
  setDeskBookingSearchResults,
} from '../../../redux/reducers/deskBookingSearchReducer'
import { BookingSearchParams } from '../types'

import {
  getDashboardBookings,
  onBookingGridViewDataLoaded,
  handleSearch,
  handleResetBookingWizardFloorPlan,
} from '../bookingLogic'
import TimePicker from '../../../shared/UI/TimePicker'
import { formatTime } from '../../../utils/date-utils'
import actionsService from '../../../services/actionsService'


interface FormFieldsSelectedCheck {
  isLocationSelected: boolean
  isFloorplanSelected: boolean
  isZoneSelected: boolean
}

type ModalProps = {
  dashboardButtonOnClick?: (click:boolean) => void
  onShowMeShared?: (bookingData: BookingSearchParams) => Promise<void>
}

const defaultShift = {
  from: combineDateAndTime(new Date(), '09:00'),
  to: combineDateAndTime(new Date(), '17:30'),
}

const fullDay = {
  from: combineDateAndTime(new Date(), '00:00'),
  to: combineDateAndTime(new Date(), '00:00'),
}

export default function BookingSearch({dashboardButtonOnClick, onShowMeShared}: ModalProps) {
  const [selectedTab, setSelectedTab] = useState(0)
  const isMobile = useMediaQuery(isMobileDown())

  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedBookingFor, setSelectedBookingFor] = useState<SelectOption>({ value: 1, displayValue: 'Myself' })
  const [selectedLocation, setSelectedLocation] = useState<BookingLocation>()
  const [selectedFloorplan, setSelectedFloorplan] = useState<BookingFloorPlan>()
  const [selectedZone, setSelectedZone] = useState<BookingZone>()
  const [selectedEmployee, setSelectedEmployee] = useState<SelectOption>()
  const userPermissions = useSelector<RootStore, string[]>(
    (state: RootStore) => state.userState.permissions
  )

  const isManager = userPermissions.includes('BookingManager')
  
  const { employees, isManagerBookingForEmployeeWithoutSelection } = useBookingFor({
    bookingFor: selectedBookingFor.value,
    selectedEmployee,
    isManager
  })

  const [blockDateRange, setBlockDateRange] = useState<
    [Date, Date] | [null, null] | [Date, null] | [null, Date]
  >([null, null])
  const [selectedFromTo, setSelectedFromTo] = useState<{ from: Date | null; to: Date | null }>(fullDay)
  const [bookingLength, setBookingLength] = useState('1')
  const [blockBookMonthsLimit, setBlockBookMonthsLimit] = useState<number>(1)

  const [isOpenFloorplanArea, setIsOpenFloorplanArea] = useState<boolean>(false)

  const dispatch = useDispatch()

  const handleSearchError = (err: string) => {
    dispatch(showErrorMessage(<BookingErrorMessage name={err} />))
  }

  const {
    locations,
    floorplans,
    zones,
    maxBookingDate,
    gridViewingDate,
    featuresForFloorPlan,
    dashboardResults,
    showGridView,
    showFloorplan,
    bookingIsLoading,
    showMeBookingID,
    focussedZoneID,
    bookingSliderPosition,
  } = useSelector((state: RootStore) => state.deskBooking)
  const { searchResults, searchParams } = useSelector(
    (state: RootStore) => state.deskBookingSearch
  )

  const { currentStep: wizardCurrentStep, weekdaysSelected: wizardWeekdaysSelected } = useSelector(
    (state: RootStore) => state.deskBookingWizard
  )

  const { employeeDetails, directReports } = useSelector((state: RootStore) => state.appSettings)
  const userInfo = useSelector((state: RootStore) => state.userState.loggedInUser)

  const [searchType, setSearchType] = useState<BookingSearchType>(BookingSearchType.SINGLE)

  const zonesForDropdown = () => {
    if (!zones || !selectedFloorplan) {
      return []
    }
    return zones
      .filter(f => f.floorPlanId === selectedFloorplan?.id)
      .filter(m => {
        const json = getJson(m.additionalInfo)
        return !json?.ui || json?.ui?.showInDropdown === true
      })
  }

  const handleResetNoDate = () => batch(() => {
    
    setBookingLength('1')
    setSelectedBookingFor({ value: ManagerBookingOptions.MYSELF, displayValue: 'Myself' })
    setSelectedLocation(locations.find(f => f.id === employeeDetails.location.id))
    setSelectedFloorplan(undefined)
    setSelectedZone(undefined)
    setSelectedEmployee(undefined)
    setBlockDateRange([null, null])
    setSelectedFromTo(fullDay)
    dispatch(setBookingFloorplanViewingDate(new Date()))
    dispatch(setBookingGridViewingDate(new Date()))
    dispatch(setBookingWizardBookingRangeState([new Date(), new Date()]))
    dispatch(setBookingWizardSelectedDatesState([]))
    dispatch(setDeskBookingSearchResults([]))
    dispatch(setDeskBookingSearchParams(undefined))
    dispatch(setBookingWizardSelectedFeatureState(undefined))
    dispatch(setBookingWizardBookingsState([]))
    dispatch(setBookingWizardFinalSelectionState([]))

    if (isManager && Boolean(employees?.length)) {
      setSelectedEmployee({ value: 0, displayValue: '' })
    }

    dispatch(setBookingSliderPosition({
      slidePosition: BookingSliderPosition.BOOKINGS,
      buttonClick: false
    }))

    if (isMobile) return

    showComponents(dispatch, {
      search: true,
      gridview: true,
      navbar: true,
      filter: false,
      blockBookingSummary: false,
      blockBookingWizard: false 
    })
  })

  const [formFieldsSelectedCheck, setFormFieldsSelectedCheck] = useState<FormFieldsSelectedCheck>({
    isLocationSelected: false,
    isFloorplanSelected: false,
    isZoneSelected: false,
  })

  useEffect(() => {
    const isLocationSelected = selectedLocation !== undefined && selectedLocation !== null
    const isFloorplanSelected = selectedFloorplan !== undefined && selectedFloorplan !== null
    const isZoneSelected = selectedZone !== undefined && selectedZone !== null

    setFormFieldsSelectedCheck({
      isLocationSelected,
      isFloorplanSelected,
      isZoneSelected,
    })
  }, [selectedLocation, selectedFloorplan, selectedZone])

  const getLatestDashboardBookings = (reset = true) =>
    getDashboardBookings(
      employeeDetails.employeeId,
      !reset ? gridViewingDate || new Date() : new Date(),
      dispatch,
      onBookingGridViewDataLoaded,
      false
    )

  const resetBookings = () => {
    if (bookingIsLoading) {
      return
    }

    batch(() => {
      dispatch(setDeskBookingLoading(true))
      dispatch(setBookingGridViewingDate(new Date()))
      dispatch(setDeskBookingDashboardResults([]))
      dispatch(setDeskBookingSearchResults([]))
    })

    getLatestDashboardBookings(true)
  }

  const resetEmployeeDashboard = () => {
    const { isFloorplanSelected, isLocationSelected, isZoneSelected } = formFieldsSelectedCheck

    if (!isFloorplanSelected || !isLocationSelected || !isZoneSelected) {
      return
    }

    const bookingsExist = dashboardResults?.results[0] || dashboardResults?.results[1]

    if (bookingsExist) {
      resetBookings()
    }
  }

  const handleStandardResetBookingWizardFloorPlan = () => {
    if (showGridView) {
      return
    }

    resetEmployeeDashboard()
    getLatestDashboardBookings()
    handleResetBookingWizardFloorPlan(dispatch)

    batch(() => {
      dispatch(setBookingFloorplanViewingDate(new Date()))
      dispatch(setBookingGridViewingDate(new Date()))

      showComponents(dispatch, {
        search: true,
        gridview: true,
        navbar: true,
        filter: false,
        blockBookingSummary: false,
        blockBookingWizard: false 
      })
    })
  }

  const handleResetWithDate = () => {
    setSelectedDate(null)
    handleResetNoDate()
  }

  const handleResetButtonWithDate = () => {
    resetBookings()
    handleResetWithDate()
    
    batch(() => {
      dispatch(setDeskBookingShowGridView(true))
      dispatch(setDeskBookingShowFloorplan(false))
      dispatch(setBookingSliderPosition({
        slidePosition: BookingSliderIndex.GRID_VIEW,
        buttonClick: false
      }))
      
      showComponents(dispatch, {
        gridview: true,
        floorplan: false,
        navbar: true,
        search: true,
        filter: false
      })
    })
  }

  useEffect(() => {
    if (isMobile && !showFloorplan) handleResetWithDate()
  }, [isMobile, showFloorplan])

  useEffect(() => {
    if (showMeBookingID !== undefined) {
      return
    }
    handleResetWithDate()
  }, [])

  useEffect(() => {
    setSelectedLocation(locations.find(f => f.id === employeeDetails.location.id))
  }, [locations, employeeDetails.location.id])

  useEffect(() => {
    if (!searchParams || !zones || !searchResults || !employeeDetails) {
      return
    }

    const populateSearchBoxes = (floorplan: BookingFloorPlan) =>
      batch(() => {
        if (!floorplan) {
          return
        }

        setSelectedDate(searchParams.date || new Date())
        setSelectedLocation(locations.find(f => f.id === employeeDetails.location.id))
        if (searchParams?.locationId) {
          setSelectedLocation(locations.find(f => f.id === searchParams?.locationId))
        }

        setSelectedFloorplan(floorplan)
        if (searchParams.zoneId) {
          setSelectedZone(zones.find(f => f.id === searchParams.zoneId))
        }
        if (!searchParams.zoneId && focussedZoneID) {
          setSelectedZone(zones.find(f => f.id === focussedZoneID))
        }
      })

    const floorplan = floorplans.find(f => f.id === searchParams?.floorplanId)
    if (!floorplan) {
      return
    }

    populateSearchBoxes(floorplan)
  }, [dispatch, searchParams, zones, floorplans, locations, focussedZoneID, employeeDetails, searchResults])

  useEffect(() => {
    if (searchParams && searchResults) {
      return
    }

    setTimeout(() => {
      if (showGridView && !bookingSliderPosition.buttonClick) {
        return
      }
      handleResetButtonWithDate()
    }, 13)
  }, [searchParams, showGridView, bookingSliderPosition, searchResults])

  const employeeLocationId = locations.find(l => l.id === employeeDetails.location.id)?.id

  useEffect(() => {
    const thisSelectedLocationId = selectedLocation?.id || employeeLocationId || 0
    const thisSelectedFloorplanId = selectedFloorplan?.id || 0
    setIsOpenFloorplanArea(
      checkIsOpenFloorplanArea(thisSelectedLocationId, thisSelectedFloorplanId)
    )
  }, [selectedLocation, selectedFloorplan, employeeLocationId])

  // Load daily bookings when component mounts
  useEffect(() => {
    const loadDailyBookings = async () => {
      try {
        dispatch(setDailyBookingsLoading(true))
        const response = await bookingService.getDailyBookings()
        dispatch(setDailyBookings(response.dailyBookings || []))
      } catch (error) {
        console.error('Failed to load daily bookings:', error)
        dispatch(setDailyBookings([]))
        // Show error message to user
        dispatch(showErrorMessage(<BookingErrorMessage name="Failed to load daily bookings" />))
      } finally {
        dispatch(setDailyBookingsLoading(false))
      }
    }

    loadDailyBookings()
  }, [dispatch])

  const disableFind = () => {
    if (isManagerBookingForEmployeeWithoutSelection) {
      return true
    }

    if (!selectedDate && !blockDateRange[BookingDateRange.FROM]) {
      return true
    }
    if (
      wizardCurrentStep !== BookingWizardSteps.STEP_0_INACTIVE &&
        wizardWeekdaysSelected.length === 0
    ) {
      return true
    }

    if (!showGridView && bookingIsLoading) {
      return true
    }

    if (!selectedFromTo.from || !selectedFromTo.to 
      || Number.isNaN(selectedFromTo.from.valueOf()) || Number.isNaN(selectedFromTo.to.valueOf())) {
      return true
    }

    const { isFloorplanSelected, isLocationSelected, isZoneSelected } = formFieldsSelectedCheck
    const isOpenFloorplanOrZone = isOpenFloorplanArea ? true : isZoneSelected

    return !(isLocationSelected && isFloorplanSelected && isOpenFloorplanOrZone)
  }

  const [showSearchLoading, setShowSearchLoading] = useState(false)
  const searchDataNotAvailable = !locations?.length || !floorplans?.length || !zones?.length

  useEffect(() => {
    if (!searchDataNotAvailable) {
      return
    }

    const showLoadingTimeId = setTimeout(() => {
      setShowSearchLoading(true)
    }, 3000)

    return () => {
      clearTimeout(showLoadingTimeId)
    }
  }, [searchDataNotAvailable])

  const isCustomTimeSlotSelected = bookingLength === BookingPeriods.CUSTOM_TIME_SLOT

  useEffect(() => {
    if (isCustomTimeSlotSelected) {
      setSearchType(BookingSearchType.SINGLE)
      setSelectedFromTo(defaultShift)
    }
  }, [isCustomTimeSlotSelected])

  useEffect(() => {
    if (searchType === BookingSearchType.BLOCK) {
      setBookingLength(BookingPeriods.FULL_DAY)
      setSelectedFromTo(FULL_DAY_RANGE)
    }
  }, [searchType])

  const renderDesktopDateInputs = () => 
    (
      <Box marginTop="25px">
        <DateRangePicker
          calendars={1}
          spacing={2}
          maxDate={maxBookingDate}
          value={blockDateRange}
          arrange="Horizontal"
          disablePast
          closeOnSelect
          disableOnOrAfterMonths={blockBookMonthsLimit}
          id="booking-date-range-picker"
          onOpen={() => {
            actionsService.userAnalytics.postEvent(
              'UIClickEvent',
              'BookingDateRangePickerClick',
              window.location.pathname,
              window.location.hostname
            )
          }}
          onChange={dateRange => batch(() => {
            const [dateFrom, dateTo] = dateRange

            handleStandardResetBookingWizardFloorPlan()
            setBlockDateRange([dateFrom, dateTo])
            if (dateFrom) {
              setSelectedDate(dateFrom)
            }
            if (Number(dateFrom) === Number(dateTo)) {
              setSearchType(_=> BookingSearchType.SINGLE)
              dispatch(setBookingWizardStepState(BookingWizardSteps.STEP_0_INACTIVE))
            } else {
              dispatch(setBookingWizardStepState(BookingWizardSteps.STEP_1_SEARCH))
              dispatch(setBookingWizardWeekdaysSelectedState([]))
              setSearchType(_=> BookingSearchType.BLOCK)
            }

            if (!showGridView) {
              batch(() => {
                dispatch(setDeskBookingLoading(true))
                dispatch(setDeskBookingDashboardResults([]))
              })
              getLatestDashboardBookings(false)
            }

            showComponents(dispatch, { gridview: true, navbar: true, search: true, filter: false })
          })}
        />
        {Number(blockDateRange[0]) !== Number(blockDateRange[1]) && blockDateRange[1] !== null && (
          <>
            <Paragraph weight="bold" style={{ margin: '16px 0' }}>
              Select Days
            </Paragraph>
            <BookingWeekdayPicker
              blockDates={blockDateRange}
            />
            <Divider />
          </>
        )}
      </Box>
    )

  const renderBookingLengthInput = () => 
    (
      <FormControl sx={{ width: '100%' }}>
        <InputLabel htmlFor="grouped-select">Booking Length</InputLabel>
        <Select
          id="grouped-select"
          label="Booking Length"
          value={bookingLength}
          MenuProps={{
            disableScrollLock: true,
          }}
          disabled={!blockDateRange[0] || !blockDateRange[1]}
          onOpen={() => {
            actionsService.userAnalytics.postEvent('UIClickEvent', 'BookingPeriodDropdownClick', window.location.pathname, window.location.hostname)
          }}
          onChange={e => {
            handleStandardResetBookingWizardFloorPlan()

            const date = selectedDate ?? new Date()

            const fromTo: {from: Date | null, to: Date | null} = { from: null, to: null }
            switch (String(e.target.value)) {
              case BookingPeriods.FULL_DAY:
                fromTo.from = combineDateAndTime(date, Time.MIDNIGHT)
                fromTo.to = combineDateAndTime(addDays(date, 1), Time.MIDNIGHT)
                setBookingLength(BookingPeriods.FULL_DAY)
                break
              case BookingPeriods.HALF_DAY_FIRST_HALF:
                fromTo.from = combineDateAndTime(date, Time.MIDNIGHT)
                fromTo.to = combineDateAndTime(date, Time.MIDDAY)
                setBookingLength(BookingPeriods.HALF_DAY_FIRST_HALF)
                break
              case BookingPeriods.HALF_DAY_SECOND_HALF:
                fromTo.from = combineDateAndTime(date, Time.MIDDAY)
                fromTo.to = combineDateAndTime(addDays(date, 1), Time.MIDNIGHT)
                setBookingLength(BookingPeriods.HALF_DAY_SECOND_HALF)
                break
              case BookingPeriods.CUSTOM_TIME_SLOT:
                fromTo.from = combineDateAndTime(date, Time.DEFAULT_SHIFT_START)
                fromTo.to = combineDateAndTime(date, Time.DEFAULT_SHIFT_END)
                setBookingLength(BookingPeriods.CUSTOM_TIME_SLOT)
                break
              default:
                break
            }

            setSelectedFromTo(fromTo)
          }}
        >
          <MenuItem value={1}>Full Day</MenuItem>
          <MenuItem value={2}>First Half</MenuItem>
          <MenuItem value={3}>Second Half</MenuItem>
          <MenuItem value={4}>Custom</MenuItem>
        </Select>
      </FormControl>
    )

  const renderCustomTimeSlot = () => 
    (
      <Box display="flex" columnGap={2} justifyContent="space-between">
        <TimePicker
          label="From"
          value={selectedFromTo.from}
          onChange={(newValue) => {
            handleStandardResetBookingWizardFloorPlan()
            setSelectedFromTo({ from: newValue, to: selectedFromTo.to })
          }}
        />
        <TimePicker
          label="To"
          value={selectedFromTo.to}
          onChange={(newValue) => {
            handleStandardResetBookingWizardFloorPlan()
            if (!newValue) {
              setSelectedFromTo({ from: null, to: selectedFromTo.to })
              return
            }
            const newValueTo = selectedFromTo.from && newValue <= selectedFromTo.from ? addDays(newValue, 1) : newValue
            setSelectedFromTo({ from: selectedFromTo.from, to: newValueTo })
          }}
        />
      </Box>
    )

  if (searchDataNotAvailable) {
    if (!showSearchLoading) {
      return null
    }

    return (
      <Box display="flex" height="470px" flexDirection="column" justifyContent="top">
        <Box display="flex" alignItems="center" flexDirection="column">
          <LoadingIndicator show alignItems="center" margin="20px" />
          <Paragraph size="16px" weight="bold" color="#2c2965">
            Loading search data...
          </Paragraph>
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Grid container spacing={1}>
        <Grid xs display="flex" alignItems="center">
          <Box flexGrow={1} p={1} paddingBottom={2} paddingTop={isMobile ? 2 : 1}>
            <Tab
              titleAndContent={
                [
                  {
                    label: 'Find a desk',
                    tabContext: <></>,
                  },
                  {
                    label: 'Employee Search',
                    tabContext: <></>,
                  },
                ] as TabDetails[]
              }
              tabChange={selectedTab}
              onChange={(newTab) => {
                setSelectedTab(newTab)
                
                // Track analytics for tab clicks
                const eventType = newTab === 0 ? 'SearchFindDeskTabClick' : 'SearchEmployeeTabClick'
                actionsService.userAnalytics
                  .postEvent('UIClickEvent', eventType, window.location.pathname, window.location.hostname)
                  .catch((error: unknown) => console.error('Analytics tracking error:', error))
              }}
              removeInactiveArrows
              sx={{
                '& .MuiTab-root': {
                  fontSize: '13px',
                  textTransform: 'none',
                },
              }}
            />
          </Box>
        </Grid>
        {isMobile && dashboardButtonOnClick &&
          <Grid item width="100px" display="flex" alignItems="center" pr={5} sx={{paddingTop: '10px !important'}}>
            <Box flexGrow={1}>
              <ButtonSmall
                sx={{padding: '2px !important', margin: '0px', marginLeft: '-8px', marginBottom: '14px', minWidth: '100px', fontWeight: 600, fontSize: '11px'}}
                label="Dashboard"
                onClick={() => dashboardButtonOnClick(false)}
              />
            </Box>
          </Grid>
        }
      </Grid>

      {selectedTab === 1 && <FindPerson mode="search" onShowMeShared={onShowMeShared} />}
      {selectedTab === 0 && (
        <>
            <Grid container spacing={2} minHeight="350px" height={isMobile ? '100%' : '540px'} alignContent="flex-start">
            <Grid item xs={12}>
              {isMobile &&
              <Box mt={2} mb={1} marginTop="10px">
                <DatePicker
                  maxDate={maxBookingDate}
                  value={selectedDate}
                  label="* When?"
                  disablePast
                  dataTestId="booking-date-picker"
              onChange={date => {
                    actionsService.userAnalytics.postEvent(
                  'UIClickEvent',
                  'BookingDatePickerClickMobile',
                  window.location.pathname,
                  window.location.hostname
                )
                if (!date || Object.prototype.toString.call(date) !== '[object Date]') {
                      return
                    }
                    handleStandardResetBookingWizardFloorPlan()
                    setSelectedDate(date)
                    setBlockDateRange([date, date])
                    setSearchType(_=> BookingSearchType.SINGLE)
                    dispatch(setBookingWizardStepState(BookingWizardSteps.STEP_0_INACTIVE))
                  }}
                />
              </Box>
              }
              {!isMobile &&
              <Grid item xs={12}>
                {renderDesktopDateInputs()}
              </Grid>
            }
            </Grid>
            <Grid item xs={12}>
              <DropdownMenu
                label="* Location"
                data={locations}
                textField="name"
                valueField="id"
                id="location"
                value={selectedLocation?.id || employeeLocationId || ''}
                onOpen={() => {
              actionsService.userAnalytics.postEvent('UIClickEvent', 'BookingLocationDropdownClick', window.location.pathname, window.location.hostname)
            }}
            onChange={e => {
                  if (selectedLocation?.id === Number(e.target.value)) {
                    return
                  }
                  handleStandardResetBookingWizardFloorPlan()
                  setSelectedLocation(
                    locations.find(f => f.id === (Number(e.target.value) || employeeLocationId))
                  )
                  setSelectedFloorplan(undefined)
                  setSelectedZone(undefined)
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DropdownMenu
                label="* Floor"
                data={floorplans.filter(
                  f => f.locationId === (selectedLocation?.id || employeeLocationId)
                )}
                textField="name"
                valueField="id"
                id="floorplan"
                value={selectedFloorplan?.id || ''}
                onOpen={() => {
              actionsService.userAnalytics.postEvent('UIClickEvent', 'BookingFloorplanDropdownClick', window.location.pathname, window.location.hostname)
            }}
            onChange={e => {
                  const thisSelectedFloorPlanId = Number(e.target.value)
                  if (selectedFloorplan?.id === thisSelectedFloorPlanId) {
                    return
                  }
                
                  handleStandardResetBookingWizardFloorPlan()
                  
                  dispatch(setDeskBookingSearchParams(undefined))
                
                  setSelectedFloorplan(floorplans.find(f => f.id === thisSelectedFloorPlanId))
                
                  const thisSelectedLocationId = selectedLocation?.id || employeeLocationId || 0
                
                  setIsOpenFloorplanArea(
                    checkIsOpenFloorplanArea(thisSelectedLocationId, thisSelectedFloorPlanId)
                  )
                
                  setSelectedZone(undefined)
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <DropdownMenu
                label={`${isOpenFloorplanArea ? '' : '* '}Zone`}
                data={isOpenFloorplanArea ? [{id: 0, name: "Any"}] : zonesForDropdown()}
                textField="name"
                valueField="id"
                disabled={!selectedFloorplan || isOpenFloorplanArea}
                id="zone"
                value={isOpenFloorplanArea ? 0 : selectedZone?.id || ''}
                onOpen={() => {
              actionsService.userAnalytics.postEvent('UIClickEvent', 'BookingZoneDropdownClick', window.location.pathname, window.location.hostname)
            }}
            onChange={e => {
                  if (selectedZone?.id === Number(e.target.value)) {
                    return
                  }
                  handleStandardResetBookingWizardFloorPlan()
                  const zone = zones.find(f => f.id === Number(e.target.value))
                  setSelectedZone(zone)
                  dispatch(setDeskBookingFocussedZoneID(zone?.id || 0))
                  
                  if (searchParams) {
                    dispatch(
                      setDeskBookingSearchParams({
                        ...searchParams,
                        zoneId: zone?.id || 0
                      })
                    )
                  }
                }}
              />
            </Grid>
            {wizardCurrentStep === BookingWizardSteps.STEP_0_INACTIVE && searchType === BookingSearchType.SINGLE && (
              <Grid item xs={12}>
                { renderBookingLengthInput() }
              </Grid>
            )}
            {isCustomTimeSlotSelected && (
              <Grid item xs={12}>
                { renderCustomTimeSlot() }
              </Grid>
            )}
            {isManager && (
              <BookingForControls
                bookingFor={selectedBookingFor.value}
                selectedEmployee={selectedEmployee}
                employees={employees}
                onBookingForChange={(bookingForValue, employee) => {
                  handleStandardResetBookingWizardFloorPlan()
                                    if (!employee) {
                    setSelectedEmployee(undefined)
                  } else if (typeof employee === 'string') {
                    setSelectedEmployee({ value: -1, displayValue: employee })
                  } else {
                    setSelectedEmployee({
                      value: employee.value,
                      displayValue: employee.displayValue.replace('Add', '').replace(/"/g, '').trim(),
                    })
                  }
                  
                  setSelectedBookingFor(BOOKING_FOR_OPTIONS.find(f => f.value === bookingForValue)!)
                }}
                useGridLayout
              />
            )}
            </Grid>
          <Grid
            container
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              zIndex: 1000,
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                marginTop: isMobile ? '50px' : '0'
              }}
            >
              <Button
                label="Find"
                disabled={disableFind()}
                fullWidth
                style={{ margin: 0, marginTop: '0px' }}
                onClick={e => batch(async () => {
                  actionsService.userAnalytics.postEvent('UIClickEvent', 'BookingFindButtonClick', window.location.pathname, window.location.hostname)
              const selectedFromTime = selectedFromTo.from
                  const selectedToTime = removeOneSecond(selectedFromTo.to ?? new Date())
                  const selectedData = {
                    selectedFromTo: {
                      from: formatTime(selectedFromTime),
                      to: formatTime(selectedToTime),
                    },
                    searchType,
                  }

                  const validBlockBookingDates =
                    blockDateRange[BookingDateRange.FROM] !== null &&
                    blockDateRange[BookingDateRange.TO] !== null

                  if (
                    selectedLocation &&
                    selectedFloorplan &&
                    (selectedDate || validBlockBookingDates)
                  ) {
                    dispatch(
                      setDeskBookingSearchParams({
                        locationId: selectedLocation?.id,
                        floorplanId: selectedFloorplan?.id,
                        employeeID: selectedEmployee?.value || employeeDetails.employeeId,
                        employeeName:
                          selectedEmployee?.displayValue ||
                          `${employeeDetails.employeeForename} ${employeeDetails.employeeSurname}`,
                        date:
                          selectedDate ||
                          (blockDateRange[BookingDateRange.FROM] &&
                            new Date(format(blockDateRange[BookingDateRange.FROM], 'yyyy-MM-dd'))) ||
                          new Date(),
                        ...(blockDateRange[BookingDateRange.TO] && {
                          dateTo: format(blockDateRange[BookingDateRange.TO], 'yyyy-MM-dd'),
                        }),
                        from: formatTime(selectedFromTime),
                        to: formatTime(selectedToTime),
                        autoSearch: false,
                        zoneId: selectedZone?.id,
                        bookingFor: selectedBookingFor?.value
                      })
                    )
                  }

                  const isValidEmployeeId = selectedEmployee && selectedEmployee?.value > 0
                  const isDifferentEmployeeIdToCurrentUser =
                    employeeDetails.employeeId !== selectedEmployee?.value

                  const thisSearchEmployeeDetails = {
                    ...employeeDetails,
                    employeeId: Number(
                      isValidEmployeeId && isDifferentEmployeeIdToCurrentUser
                        ? selectedEmployee?.value
                        : employeeDetails.employeeId
                    ),
                  }

                  dispatch(
                    setBookingSliderPosition({
                      slidePosition: BookingSliderPosition.FLOOR_PLAN,
                      buttonClick: false,
                    })
                  )
                  dispatch(setDeskBookingFloorplanManuallySelected(false))
                  dispatch(setDeskBookingFocussedZoneID(selectedZone?.id || 0))

                  if (wizardCurrentStep === BookingWizardSteps.STEP_0_INACTIVE) {
                    if (!selectedDate) {
                      return
                    }
                    dispatch(setBookingFloorplanViewingDate(selectedDate))
                    showComponents(dispatch, { floorplan: true, navbar: true, search: true })
                    if (!selectedLocation || !selectedFloorplan) {
                      return
                    }

                    const singleQuery = true
                    await handleSearch(
                      dispatch,
                      handleSearchError,
                      singleQuery,
                      zones,
                      featuresForFloorPlan,
                      thisSearchEmployeeDetails,
                      userInfo,
                      selectedData,
                      selectedLocation.id,
                      selectedFloorplan.id,
                      null,
                      selectedDate
                    )

                  } else {
                    if (
                      !selectedLocation ||
                      !selectedFloorplan ||
                      !blockDateRange[BookingDateRange.FROM] ||
                      !blockDateRange[BookingDateRange.TO]
                    ) {
                      return
                    }

                    showComponents(dispatch, {
                      floorplan: true,
                      search: true,
                      blockBookingWizard: true,
                    })

                    dispatch(setBookingFloorplanViewingDate(blockDateRange[BookingDateRange.FROM]))
                    dispatch(setBookingWizardSelectedDatesState(getDatesFromRange(blockDateRange)))
                    dispatch(setBookingWizardStepState(BookingWizardSteps.STEP_2_SELECT_DESK))
                    dispatch(setBookingWizardBookingRangeState(blockDateRange))

                    const singleQuery = false
                    await handleSearch(
                      dispatch,
                      handleSearchError,
                      singleQuery,
                      zones,
                      featuresForFloorPlan,
                      employeeDetails,
                      userInfo,
                      selectedData,
                      selectedLocation.id,
                      selectedFloorplan.id,
                      null,
                      blockDateRange[BookingDateRange.FROM],
                      blockDateRange[BookingDateRange.TO]
                    )

                  }
                })}
              />
              <BookinResetSearch
            id="reset"
            onClick={() => {
              actionsService.userAnalytics.postEvent('UIClickEvent', 'BookingResetButtonClick', window.location.pathname, window.location.hostname)
              handleResetButtonWithDate()
            }}>
                <LoopOutlinedIcon style={{ width: '18px', marginRight: '5px' }} />
                Reset
              </BookinResetSearch>
            </Grid>
          </Grid>
        </>
      )}
    </>
  )
}
